import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { Control, FieldPath, FieldValues, useFormContext, useWatch } from 'react-hook-form';

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from '@/components/core/combobox';
import { SelectItem } from '@/components/core/select';
import { DefaultFormField } from '@/components/forms/default-form-field';
import { SelectFormField } from '@/components/forms/select-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';

export interface RunConfigFormValues {
  accountId: string;
  locationId: string;
  carrierServiceIds: string[];
}

function getLocationPlaceholder(accountId: string | undefined, isLoading: boolean) {
  if (!accountId) return 'Select an account first';
  if (isLoading) return 'Loading locations...';
  return 'Select a location';
}

function getCarrierServicesChipsInputPlaceholder(selectedCount: number, isLoading: boolean) {
  if (selectedCount > 0) return '';
  if (isLoading) return 'Loading carrier services...';
  return 'Select carrier services';
}

type Props<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  accountFieldName: FieldPath<TFieldValues>;
  locationFieldName: FieldPath<TFieldValues>;
  carrierServicesFieldName: FieldPath<TFieldValues>;
};

export function RunConfigFields<TFieldValues extends FieldValues>({
  control,
  accountFieldName,
  locationFieldName,
  carrierServicesFieldName,
}: Props<TFieldValues>) {
  const { accountsGateway, shippingGateway } = useGateways();
  const { setValue } = useFormContext<TFieldValues>();
  const anchorRef = useComboboxAnchor();

  const selectedAccountId = useWatch({ control, name: accountFieldName }) as string | undefined;

  const { data: currentUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: ({ signal }) => accountsGateway.getCurrentUser({ signal }),
  });

  const accountOptions = useMemo(() => {
    // PLATFORM memberships carry a null accountId — the PLATFORM role authorises
    // access to this UI but doesn't bind to a customer account — so the
    // `m.accountId && m.accountName` filter naturally excludes them.
    const memberships = currentUser?.memberships ?? [];
    const seen = new Set<string>();
    return memberships
      .filter((m) => m.accountId && m.accountName)
      .filter((m) => {
        const id = m.accountId as string;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map((m) => ({ id: m.accountId as string, name: m.accountName as string }));
  }, [currentUser]);

  const { data: locationOptions = [], isLoading: isLoadingLocations } = useQueryWithInput({
    parentKey: 'locations',
    input: { accountId: selectedAccountId, scope: 'simulation-run-config' },
    query: (input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getLocations({ accountId: input.accountId, cursor, take }, { signal }),
      ),
    enabled: Boolean(selectedAccountId),
    select: (locations) => {
      if (!selectedAccountId) return [];

      const membership = currentUser?.memberships?.find((m) => m.accountId === selectedAccountId);
      const membershipLocationIds = membership?.locationIds;

      const filtered =
        membershipLocationIds && membershipLocationIds.length > 0
          ? locations.filter((l) => l.id && membershipLocationIds.includes(l.id))
          : locations;

      return filtered.filter((l) => l.id && l.name);
    },
  });

  // Reset location when the selected account changes — otherwise the user can
  // submit a location that belonged to the previous account. Uses a ref rather
  // than a state dep so the effect fires only on real transitions, not the
  // initial render.
  const previousAccountIdRef = useRef<string | undefined>(selectedAccountId);
  useEffect(() => {
    if (previousAccountIdRef.current !== selectedAccountId) {
      if (previousAccountIdRef.current !== undefined) {
        setValue(locationFieldName, '' as TFieldValues[FieldPath<TFieldValues>], {
          shouldValidate: false,
          shouldDirty: true,
        });
      }
      previousAccountIdRef.current = selectedAccountId;
    }
  }, [selectedAccountId, locationFieldName, setValue]);

  const { data: carrierServiceItems = [], isLoading: isLoadingCarrierServices } = useQueryWithInput(
    {
      parentKey: 'carrierServices',
      input: { scope: 'simulation-run-config' },
      query: (_input, { signal }) =>
        fetchAllPages(({ cursor, take }) =>
          shippingGateway.getCarrierServices({ cursor, take }, { signal }),
        ),
      select: (carrierServices) =>
        carrierServices.filter((c) => c.id && c.name).map((c) => ({ label: c.name, value: c.id })),
    },
  );

  const carrierServiceLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    carrierServiceItems.forEach((item) => {
      map[item.value] = item.label;
    });
    return map;
  }, [carrierServiceItems]);

  return (
    <>
      <SelectFormField
        control={control}
        name={accountFieldName}
        label="Account"
        placeholder={isLoadingUser ? 'Loading accounts...' : 'Select an account'}
        disabled={isLoadingUser || accountOptions.length === 0}
        className="w-full"
      >
        {accountOptions.map((option) => (
          <SelectItem key={option.id} value={option.id}>
            {option.name}
          </SelectItem>
        ))}
        {!isLoadingUser && accountOptions.length === 0 && (
          <SelectItem value="no-accounts" disabled>
            No platform accounts available
          </SelectItem>
        )}
      </SelectFormField>

      <SelectFormField
        control={control}
        name={locationFieldName}
        label="Location"
        placeholder={getLocationPlaceholder(selectedAccountId, isLoadingLocations)}
        disabled={!selectedAccountId || isLoadingLocations}
        className="w-full"
      >
        {locationOptions.map((location) => (
          <SelectItem key={location.id} value={location.id}>
            {location.name}
          </SelectItem>
        ))}
        {selectedAccountId && !isLoadingLocations && locationOptions.length === 0 && (
          <SelectItem value="no-locations" disabled>
            No locations available for this account
          </SelectItem>
        )}
      </SelectFormField>

      <DefaultFormField
        control={control}
        name={carrierServicesFieldName}
        label="Carrier services"
        description="Pick one or more carrier services to quote with"
        render={({ field }) => {
          const values = Array.isArray(field.value) ? (field.value as string[]) : [];
          return (
            <Combobox
              items={carrierServiceItems}
              multiple
              disabled={isLoadingCarrierServices}
              value={values}
              name={field.name}
              onValueChange={(nextValue) => {
                field.onChange(Array.isArray(nextValue) ? nextValue : []);
              }}
            >
              <ComboboxChips ref={anchorRef}>
                <ComboboxValue>
                  {(selectedValues: string[]) => (
                    <>
                      {selectedValues.map((value) => (
                        <ComboboxChip key={value}>
                          {carrierServiceLabelById[value] ?? value}
                        </ComboboxChip>
                      ))}
                      <ComboboxChipsInput
                        aria-label="Carrier services"
                        placeholder={getCarrierServicesChipsInputPlaceholder(
                          selectedValues.length,
                          isLoadingCarrierServices,
                        )}
                        onBlur={field.onBlur}
                        disabled={field.disabled}
                      />
                    </>
                  )}
                </ComboboxValue>
              </ComboboxChips>
              <ComboboxContent anchor={anchorRef}>
                <ComboboxEmpty>No carrier services found.</ComboboxEmpty>
                <ComboboxList>
                  {(item: { label: string; value: string }) => (
                    <ComboboxItem key={item.value} value={item.value}>
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          );
        }}
      />
    </>
  );
}
